import {
  isReservationSlotInterval,
  RESERVATION_DEFAULT_SLOT_INTERVAL_MIN,
} from '@/interfaces/IReservationSettings';
import type {
  IReservationArrivalTime,
  IReservationShift,
  TReservationSlotInterval,
} from '@/interfaces/IReservationSettings';

/** A shift as an older API build may still return it: no interval, or one outside the set. */
export type TShiftFromApi = Omit<IReservationShift, 'slotIntervalMin'> & {
  slotIntervalMin?: number | null;
};

/** Boundary rule: anything that is not an allowed interval reads as the default. */
export function withShiftInterval(shift: TShiftFromApi): IReservationShift {
  return {
    ...shift,
    slotIntervalMin: isReservationSlotInterval(shift.slotIntervalMin)
      ? shift.slotIntervalMin
      : RESERVATION_DEFAULT_SLOT_INTERVAL_MIN,
  };
}

export interface IFixedTimesSummary {
  /** The first times joined for a card line. */
  shown: string;
  /** How many times the line leaves out. */
  hidden: number;
}

export function fixedTimesSummary(
  times: readonly IReservationArrivalTime[] | null,
  limit = 6
): IFixedTimesSummary {
  const list = (times ?? []).map((row) => row.time);
  return {
    shown: list.slice(0, limit).join(' · '),
    hidden: Math.max(0, list.length - limit),
  };
}

export type TShiftIntervalSummary =
  | { kind: 'uniform'; minutes: TReservationSlotInterval }
  | { kind: 'mixed' };

/** Uniform only when at least one shift exists and every shift steps the same grid. */
export function shiftIntervalSummary(
  shifts: readonly Pick<IReservationShift, 'arrivalTimes' | 'slotIntervalMin'>[]
): TShiftIntervalSummary {
  if (!shifts.length || shifts.some((shift) => shift.arrivalTimes)) {
    return { kind: 'mixed' };
  }
  const [first] = shifts;
  return shifts.every(
    (shift) => shift.slotIntervalMin === first.slotIntervalMin
  )
    ? { kind: 'uniform', minutes: first.slotIntervalMin }
    : { kind: 'mixed' };
}
